import { Activity, CircleCheckBig, Clock, MinusCircle, TriangleAlert } from 'lucide-react'
import React from 'react'

import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { type BrokersTaskTypeSummaryTaskStatistics } from 'utils/web_api.schemas'

interface ProgressBarProps {
  stats: BrokersTaskTypeSummaryTaskStatistics
}

export const ProgressBar: React.FC<ProgressBarProps> = ({ stats }) => {
  const { total, pending, processing, completed, failed, excluded } = stats

  if (total === 0) {
    return (
      <div className='space-y-2'>
        <div className='w-full bg-gray-200 rounded-full h-2'>
          <div className='bg-gray-400 h-2 rounded-full w-full' />
        </div>
        <div className='text-xs text-gray-500 text-center'>No tasks</div>
      </div>
    )
  }

  const completedPercent = (completed / total) * 100
  const pendingPercent = (pending / total) * 100
  const processingPercent = (processing / total) * 100
  const failedPercent = (failed / total) * 100
  const excludedPercent = (excluded / total) * 100

  return (
    <div className='space-y-2'>
      <div className='w-full bg-gray-200 rounded-full h-2 flex overflow-hidden'>
        {completed > 0 && (
          <div
            className='bg-emerald-400 h-2'
            style={{ width: `${completedPercent}%` }}
          />
        )}
        {pending > 0 && (
          <div
            className='bg-amber-400 h-2'
            style={{ width: `${pendingPercent}%` }}
          />
        )}
        {processing > 0 && (
          <div
            className='bg-blue-400 h-2'
            style={{ width: `${processingPercent}%` }}
          />
        )}
        {failed > 0 && (
          <div
            className='bg-rose-400 h-2'
            style={{ width: `${failedPercent}%` }}
          />
        )}
        {excluded > 0 && (
          <div
            className='bg-gray-400 h-2'
            style={{ width: `${excludedPercent}%` }}
          />
        )}
      </div>
      <div className='flex justify-between items-center text-xs text-gray-600'>
        <div className='flex items-center gap-1'>
          <Activity className='w-4 h-4' />
          <span>{total}</span>
        </div>
        <div className='flex items-center gap-3'>
          <div className='flex items-center gap-1 text-emerald-400'>
            <CircleCheckBig className='w-4 h-4' />
            <span>{completed}</span>
          </div>
          <div className='flex items-center gap-1 text-amber-400'>
            <Clock className='w-4 h-4' />
            <span>{pending}</span>
          </div>
          <div className='flex items-center gap-1 text-blue-400'>
            <Activity className='w-4 h-4' />
            <span>{processing}</span>
          </div>
          <div className='flex items-center gap-1 text-rose-400'>
            <TriangleAlert className='w-4 h-4' />
            <span>{failed}</span>
          </div>
          <div className='flex items-center gap-1 text-gray-400'>
            <MinusCircle className='w-4 h-4' />
            <span>{excluded}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export const ProgressBarSkeleton: React.FC = () => (
  <div className='space-y-2'>
    <SkeletonItem widthClass='w-full' heightClass='h-2' />
    <div className='flex justify-between items-center'>
      <SkeletonItem widthClass='w-8' heightClass='h-4' />
      <div className='flex items-center gap-3'>
        <SkeletonItem widthClass='w-8' heightClass='h-4' />
        <SkeletonItem widthClass='w-8' heightClass='h-4' />
        <SkeletonItem widthClass='w-8' heightClass='h-4' />
        <SkeletonItem widthClass='w-8' heightClass='h-4' />
        <SkeletonItem widthClass='w-8' heightClass='h-4' />
      </div>
    </div>
  </div>
)
